import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { updateEmployee } from '../services/api.ts';
import { useCredentials, useEmployees, useHospitals, writeCachedRows } from '../data/queries.ts';
import { queryKeys } from '../data/queryKeys.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSubscription } from '../contexts/SubscriptionContext.tsx';
import { matchesNameQuery } from '../utils/nameFilter.ts';
import LockToggle from './LockToggle.tsx';

// ---------------------------------------------------------------------------
// Credentials table — the one table on the Providers page.
//
// Two matrices — Anesthesiologists and CRNAs — of EVERY provider the group owns (not just today's
// workers). Names run down the left, alphabetical by last name (sticky). The sites the group covers run
// across the top (sticky, scrolls left/right). A green check sits where a provider is credentialed at
// that site.
//
// SITE COLUMNS = the credential catalog + EVERY hospital name + any credential already on a card. So
// when the group adds a hospital, it automatically shows up here as a credential site too.
//
// EDITING (admins only): each matrix starts LOCKED (read-only). Click "Locked" to unlock; then every cell
// is a clickable toggle — click an empty box to credential someone at that site, click a green check to
// remove it. Each change saves immediately to that person's card. The checkbox under a site name
// credentials (or un-credentials) everyone in that list at once, with Undo. Auto-locks after 30 seconds
// idle (or lock manually). New providers are added with "Create Provider" above. Clicking a provider's
// name opens their full card in the edit dialog (admins only; the name is plain text for everyone else),
// whether or not the list is locked — the lock guards the credential cells, not the card.
//
// DATA: the employee list is the shared cache (data/queries.ts), so a save from the edit dialog and this
// table never disagree. Unsaved edits are held as per-person PENDING overrides drawn on top of the cached rows; when a
// save lands, the confirmed row is written into the cache and its override dropped; when it fails, the
// override is dropped, which reverts the cell. Holding edits beside the cache (rather than in it) means a
// background refetch triggered by an earlier save can't briefly undo a change that is still saving.
// ---------------------------------------------------------------------------

interface EmployeeLike { employeeId: string; employeeType: string; firstName: string; lastName: string; credentials?: string[]; homeHospitalId?: string; [k: string]: any; }
type MatrixType = 'doctor' | 'nurse';
/** An edit not yet confirmed by the server. `version` lets an older save's reply ignore a newer edit. */
interface PendingCredentials { credentials: string[]; version: number; }

const AUTO_LOCK_MS = 30000;
const CHECK_GREEN = '#1f9d55';
const EMPTY: EmployeeLike[] = [];
const EMPTY_CATALOG: string[] = [];
const EMPTY_HOSPITALS: { name: string }[] = [];

interface CredentialsMatrixProps {
  /** The Providers page search; narrows both matrices by first or last name. */
  nameFilter?: string;
  /** Called with the provider whose name was clicked; without it (or for read-only viewers) names are plain text. */
  onEditProvider?: (employee: EmployeeLike) => void;
}

const CredentialsMatrix: React.FC<CredentialsMatrixProps> = ({ nameFilter = '', onEditProvider }) => {
  const { isAdmin } = useAuth();
  const { canWrite } = useSubscription();
  // Same rule as AdminOnly: a read-only account, or a tenant whose subscription lapsed, cannot edit.
  const canEdit = isAdmin && canWrite;

  const queryClient = useQueryClient();
  const employeesQuery = useEmployees();
  const catalogQuery = useCredentials();
  const hospitalsQuery = useHospitals();
  const cachedEmployees = (employeesQuery.data ?? EMPTY) as EmployeeLike[];
  const catalog = catalogQuery.data ?? EMPTY_CATALOG;
  const hospitals = hospitalsQuery.data ?? EMPTY_HOSPITALS;

  const [note, setNote] = useState<{ text: string; kind: 'info' | 'error' } | null>(null);

  // Each list (Anesthesiologists / CRNAs) locks & unlocks on its own.
  const [lockedByType, setLockedByType] = useState<Record<MatrixType, boolean>>({ doctor: true, nurse: true });
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Snapshot of the last bulk change so it can be undone (restores each affected person's prior credentials).
  const [lastBulk, setLastBulk] = useState<{ site: string; add: boolean; groupLabel: string; type: MatrixType; snapshot: { employeeId: string; credentials: string[] }[] } | null>(null);
  const lockTimers = useRef<Record<MatrixType, ReturnType<typeof setTimeout> | null>>({ doctor: null, nurse: null });

  // Pending (unsaved) credential edits, by employee. State drives rendering; the ref gives rapid clicks
  // the latest value without waiting for a re-render.
  const [pending, setPendingState] = useState<Record<string, PendingCredentials>>({});
  const pendingRef = useRef<Record<string, PendingCredentials>>({});
  const versionRef = useRef(0);
  const setPending = (update: (prev: Record<string, PendingCredentials>) => Record<string, PendingCredentials>) => {
    pendingRef.current = update(pendingRef.current);
    setPendingState(pendingRef.current);
  };

  // What the grid shows: the cached rows with any pending edits drawn on top.
  const employees = useMemo(
    () => cachedEmployees.map(e => (pending[e.employeeId] ? { ...e, credentials: pending[e.employeeId].credentials } : e)),
    [cachedEmployees, pending]
  );
  const employeesRef = useRef<EmployeeLike[]>(cachedEmployees);
  employeesRef.current = cachedEmployees;
  const currentCredentials = (empId: string): string[] | null => {
    const p = pendingRef.current[empId];
    if (p) return p.credentials;
    const e = employeesRef.current.find(x => x.employeeId === empId);
    return e ? (e.credentials || []) : null;
  };

  useEffect(() => () => { (['doctor', 'nurse'] as const).forEach(t => { const tm = lockTimers.current[t]; if (tm) clearTimeout(tm); }); }, []);
  const bumpAutoLock = (type: MatrixType) => {
    const existing = lockTimers.current[type];
    if (existing) clearTimeout(existing);
    lockTimers.current[type] = setTimeout(() => {
      setLockedByType(prev => ({ ...prev, [type]: true }));
    }, AUTO_LOCK_MS);
  };
  const unlock = (type: MatrixType) => {
    if (!canEdit) return;
    setLockedByType(prev => ({ ...prev, [type]: false }));
    bumpAutoLock(type);
  };
  const lockNow = (type: MatrixType) => {
    const tm = lockTimers.current[type];
    if (tm) clearTimeout(tm);
    setLockedByType(prev => ({ ...prev, [type]: true }));
  };
  // A matrix is editable only while unlocked AND the viewer may edit.
  const isLocked = (type: MatrixType) => !canEdit || lockedByType[type];

  // Site columns: credential catalog ∪ hospital names ∪ any credential already on a card.
  const sites = useMemo(() => {
    const s = new Set<string>();
    for (const c of catalog) if (c && c.trim()) s.add(c);
    for (const h of hospitals) if (h.name && h.name.trim()) s.add(h.name);
    for (const e of employees) for (const c of e.credentials || []) s.add(c);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [catalog, hospitals, employees]);

  const doctors = employees.filter(e => e.employeeType === 'doctor');
  const crnas = employees.filter(e => e.employeeType === 'nurse' || e.employeeType.startsWith('nurse-student'));

  const byLastName = (a: EmployeeLike, b: EmployeeLike) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName);
  const filtering = nameFilter.trim().length > 0;

  /**
   * Save `credentials` to one person's card. The edit shows immediately (pending override); success writes
   * the confirmed row into the shared cache, failure drops the override so the cell reverts.
   * Resolves true when the save landed.
   */
  const saveCredentials = async (empId: string, credentials: string[]): Promise<boolean> => {
    const base = employeesRef.current.find(e => e.employeeId === empId);
    if (!base) return false;
    const version = ++versionRef.current;
    setPending(prev => ({ ...prev, [empId]: { credentials, version } }));
    const { employeeId, ...rest } = { ...base, credentials };
    try {
      await updateEmployee(employeeId, rest as any);
      writeCachedRows<EmployeeLike>(queryClient, queryKeys.employees(), list =>
        list.map(x => (x.employeeId === empId ? { ...x, credentials } : x))
      );
      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally {
      // Drop the override only if no newer edit for this person was made while this one was saving.
      setPending(prev => {
        if (prev[empId]?.version !== version) return prev;
        const { [empId]: _done, ...others } = prev;
        return others;
      });
    }
  };

  // Toggle one credential on one person and save it to their card immediately.
  const toggleCredential = (empId: string, site: string, type: MatrixType) => {
    if (!canEdit) return;
    if (isLocked(type)) return;
    const key = `${empId}|${site}`;
    if (saving.has(key)) return;
    bumpAutoLock(type);
    const current = currentCredentials(empId);
    if (!current) return;
    const held = new Set(current);
    if (held.has(site)) held.delete(site); else held.add(site);

    setSaving(s => new Set(s).add(key));
    saveCredentials(empId, [...held])
      .then(ok => setNote(ok ? null : { text: 'Could not save that change — please try again.', kind: 'error' }))
      .finally(() => setSaving(s => { const n = new Set(s); n.delete(key); return n; }));
  };

  // Run async work over a list in small parallel chunks (so a bulk change doesn't fire hundreds of
  // requests at once).
  const runChunked = async <T,>(items: T[], size: number, fn: (item: T) => Promise<void>) => {
    for (let i = 0; i < items.length; i += size) {
      await Promise.all(items.slice(i, i + size).map(fn));
    }
  };

  // Save a batch of people's credentials; returns how many failed.
  const saveMany = async (changes: { employeeId: string; credentials: string[] }[]): Promise<number> => {
    let failures = 0;
    await runChunked(changes, 12, async ({ employeeId, credentials }) => {
      if (!(await saveCredentials(employeeId, credentials))) failures++;
    });
    return failures;
  };

  // Credential (or un-credential) EVERY person in a matrix at one site in a single action — hospitals
  // often credential the entire group at once. Confirms first, updates optimistically, saves each card.
  const bulkSetSite = async (people: EmployeeLike[], site: string, add: boolean, groupLabel: string, type: MatrixType) => {
    if (!canEdit) return;
    if (isLocked(type) || bulkBusy) return;
    const targets = people.filter(p => (add ? !(p.credentials || []).includes(site) : (p.credentials || []).includes(site)));
    if (targets.length === 0) return;
    const verb = add ? 'Credential' : 'Remove the credential for';
    if (!window.confirm(`${verb} all ${people.length} ${groupLabel} at "${site}"?`)) return;
    bumpAutoLock(type);
    // Snapshot the affected people's CURRENT credentials so the whole change can be undone.
    const snapshot = targets.map(t => ({ employeeId: t.employeeId, credentials: [...(t.credentials || [])] }));
    const changes = targets.map(t => {
      const held = new Set(t.credentials || []);
      if (add) held.add(site); else held.delete(site);
      return { employeeId: t.employeeId, credentials: [...held] };
    });
    setBulkBusy(true);
    setNote(null);
    const failures = await saveMany(changes);
    setBulkBusy(false);
    setLastBulk({ site, add, groupLabel, type, snapshot }); // enable Undo
    if (failures) {
      // Re-sync from the server so the grid reflects what actually saved.
      void queryClient.invalidateQueries({ queryKey: queryKeys.employees() });
      setNote({ text: `${failures} of ${changes.length} didn't save — the page has been refreshed to show what stuck.`, kind: 'error' });
    } else {
      setNote({ text: `${add ? 'Credentialed' : 'Removed'} all ${targets.length} ${groupLabel} ${add ? 'at' : 'from'} "${site}".`, kind: 'info' });
    }
  };

  // Undo the last bulk change — restore each affected person's credentials to the saved snapshot.
  const undoBulk = async () => {
    if (!canEdit) return;
    if (!lastBulk || bulkBusy) return;
    if (!window.confirm(`Undo the last change and restore all ${lastBulk.snapshot.length} ${lastBulk.groupLabel} to how "${lastBulk.site}" was before?`)) return;
    bumpAutoLock(lastBulk.type);
    setBulkBusy(true);
    setNote(null);
    const failures = await saveMany(lastBulk.snapshot.map(s => ({ employeeId: s.employeeId, credentials: [...s.credentials] })));
    setBulkBusy(false);
    if (failures) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.employees() });
      setNote({ text: `Undo partly failed (${failures}) — the page has been refreshed to show the current state.`, kind: 'error' });
    } else {
      setNote({ text: `Reverted — restored ${lastBulk.snapshot.length} ${lastBulk.groupLabel} to how "${lastBulk.site}" was before.`, kind: 'info' });
    }
    setLastBulk(null);
  };

  const NAME_W = 150; // sticky name column width (smaller so more site columns fit)
  // Sticky cells MUST be opaque or the scrolling table shows through them. Layer the tint over the
  // opaque surface token (same trick the Board's room-cell uses).
  const HEAD_BG = 'linear-gradient(var(--tint-header), var(--tint-header)), var(--bg-surface)';
  const nameBgFor = (zebra: boolean) => zebra ? 'linear-gradient(var(--tint-zebra), var(--tint-zebra)), var(--bg-surface)' : 'var(--bg-surface)';

  const renderCell = (e: EmployeeLike, site: string, zebra: boolean, type: MatrixType, matrixLocked: boolean) => {
    const ok = (e.credentials || []).includes(site);
    const key = `${e.employeeId}|${site}`;
    const isSaving = saving.has(key);
    const bg = zebra ? 'var(--tint-zebra)' : 'transparent';
    if (matrixLocked) {
      return (
        <td key={site} style={{ background: bg, borderRight: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', padding: '2px 3px', textAlign: 'center' }}>
          {ok && (
            <span title={`Credentialed at ${site}`} aria-label={`${e.firstName} ${e.lastName} is credentialed at ${site}`} style={{ display: 'inline-block', minWidth: '30px', padding: '1px 4px', borderRadius: '4px', background: CHECK_GREEN, color: '#fff', fontWeight: 800, fontSize: '0.82em' }}>✓</span>
          )}
        </td>
      );
    }
    return (
      <td key={site} style={{ background: bg, borderRight: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', padding: '2px 3px', textAlign: 'center' }}>
        <button
          type="button"
          disabled={isSaving}
          onClick={() => toggleCredential(e.employeeId, site, type)}
          aria-pressed={ok}
          aria-label={`${e.firstName} ${e.lastName} at ${site}`}
          title={ok ? `Click to remove credential at ${site}` : `Click to credential ${e.firstName} ${e.lastName} at ${site}`}
          style={{
            minWidth: '30px',
            padding: '1px 4px',
            borderRadius: '4px',
            fontWeight: 800,
            fontSize: '0.82em',
            cursor: isSaving ? 'default' : 'pointer',
            opacity: isSaving ? 0.5 : 1,
            color: ok ? '#fff' : 'var(--text-faint)',
            background: ok ? CHECK_GREEN : 'transparent',
            border: ok ? '1px solid rgba(0,0,0,0.15)' : '1px dashed var(--border-soft)',
            transition: 'background 0.1s',
          }}
        >
          {ok ? '✓' : '+'}
        </button>
      </td>
    );
  };

  const renderMatrix = (title: string, people: EmployeeLike[], accent: string, type: MatrixType) => {
    const rows = people.filter(p => matchesNameQuery(p, nameFilter)).sort(byLastName);
    const mlocked = isLocked(type);
    return (
      <div key={title} style={{ marginBottom: '22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>{title}</h3>
          <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>{rows.length}{filtering ? ` of ${people.length}` : ''}</span>
          {canEdit && (
            <LockToggle locked={lockedByType[type]} onChange={(nextLocked) => (nextLocked ? lockNow(type) : unlock(type))} />
          )}
        </div>
        <div style={{ border: '1px solid var(--border-soft)', borderRadius: '8px', overflow: 'auto', maxHeight: '62vh' }}>
          {/* overflow:visible overrides the global `.App table { overflow: hidden }` (App.css) — otherwise the
              table becomes its OWN scroll container and the sticky header/name cells can never pin to the
              scrollable div above. Same reason `.App .board-table table` overrides it on the board. */}
          <table aria-label={`${title} credentials`} style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.74em', overflow: 'visible' }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, top: 0, zIndex: 3, background: HEAD_BG, borderBottom: '2px solid ' + accent, borderRight: '1px solid var(--border-soft)', textAlign: 'left', padding: '5px 8px', minWidth: NAME_W, width: NAME_W }}>
                  Full Name
                </th>
                {sites.map(site => {
                  const have = people.reduce((n, p) => n + ((p.credentials || []).includes(site) ? 1 : 0), 0);
                  const allHave = people.length > 0 && have === people.length;
                  const someHave = have > 0 && have < people.length;
                  return (
                    <th key={site} title={site} style={{ position: 'sticky', top: 0, zIndex: 2, background: HEAD_BG, borderBottom: '2px solid ' + accent, borderRight: '1px solid var(--border-subtle)', padding: '4px 6px', whiteSpace: 'nowrap', fontSize: '0.9em', fontWeight: 700, color: 'var(--text-secondary)', minWidth: '56px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                        <span>{site}</span>
                        {!mlocked && (
                          <input
                            type="checkbox"
                            checked={allHave}
                            ref={el => { if (el) el.indeterminate = someHave; }}
                            disabled={bulkBusy}
                            onChange={() => bulkSetSite(people, site, !allHave, title.toLowerCase(), type)}
                            title={`Credential ALL ${title.toLowerCase()} at ${site}`}
                            aria-label={`Credential all ${title.toLowerCase()} at ${site}`}
                            style={{ cursor: bulkBusy ? 'default' : 'pointer', width: '15px', height: '15px', margin: 0 }}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((e, i) => {
                const zebra = i % 2 === 1;
                return (
                  <tr key={e.employeeId}>
                    <td style={{ position: 'sticky', left: 0, zIndex: 1, background: nameBgFor(zebra), borderRight: '1px solid var(--border-soft)', borderBottom: '1px solid var(--border-subtle)', padding: '3px 8px', fontWeight: 600, whiteSpace: 'nowrap', minWidth: NAME_W, width: NAME_W }}>
                      {canEdit && onEditProvider ? (
                        <button
                          type="button"
                          onClick={() => onEditProvider(e)}
                          aria-label={`Edit ${e.firstName} ${e.lastName}`}
                          title={`Edit ${e.firstName} ${e.lastName}`}
                          style={{ padding: 0, border: 'none', background: 'none', font: 'inherit', fontWeight: 600, color: 'var(--or-blue-mid)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                        >
                          {e.lastName}, {e.firstName}
                        </button>
                      ) : (
                        <>{e.lastName}, {e.firstName}</>
                      )}
                      {e.employeeType.startsWith('nurse-student') && <span style={{ marginLeft: '6px', fontSize: '0.72em', color: 'var(--text-muted)', fontWeight: 700 }}>SRNA</span>}
                    </td>
                    {sites.map(site => renderCell(e, site, zebra, type, mlocked))}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={sites.length + 1} style={{ padding: '10px', color: 'var(--text-faint)' }}>No matching {title.toLowerCase()}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ fontSize: '0.78em', color: 'var(--text-muted)', marginBottom: '10px' }}>
        Every provider the group owns. Site columns are your credential list <strong>plus every hospital</strong> — add a hospital and it shows up here automatically.
        A green ✓ means they’re credentialed at that site.
        {canEdit && onEditProvider && <> Click a name to edit that provider.</>}
        {canEdit && (
          <> Use the <strong>toggle next to each list</strong> (Anesthesiologists / CRNAs) to unlock it — then click a box to add (＋ → ✓) or remove (✓) a credential, or use the checkbox under a site name to credential everyone in that list at once. Changes save right away.</>
        )}
      </div>

      {note && (
        <div role={note.kind === 'error' ? 'alert' : 'status'} style={{ marginBottom: '10px', fontSize: '0.85em', padding: '6px 10px', borderRadius: '6px', background: note.kind === 'error' ? 'rgba(220,38,38,0.10)' : 'rgba(31,157,85,0.10)', color: note.kind === 'error' ? 'var(--danger-text, #dc2626)' : 'var(--success-text, #157347)', border: '1px solid ' + (note.kind === 'error' ? 'rgba(220,38,38,0.3)' : 'rgba(31,157,85,0.3)') }}>
          {note.text}
        </div>
      )}
      {lastBulk && canEdit && (
        <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '0.82em', padding: '6px 10px', borderRadius: '6px', background: 'var(--bg-surface-raised)', border: '1px solid var(--border-soft)' }}>
          <span style={{ color: 'var(--text-muted)' }}>
            Last bulk change: {lastBulk.add ? 'credentialed' : 'removed'} all {lastBulk.groupLabel} {lastBulk.add ? 'at' : 'from'} “{lastBulk.site}”.
          </span>
          <button type="button" onClick={undoBulk} disabled={bulkBusy} style={{ padding: '3px 12px', borderRadius: '6px', border: '1px solid var(--amber)', background: 'var(--amber-soft, rgba(245,158,11,0.12))', color: 'var(--amber)', fontWeight: 700, cursor: bulkBusy ? 'default' : 'pointer' }}>
            ↺ Undo
          </button>
          <button type="button" onClick={() => setLastBulk(null)} style={{ padding: '3px 8px', border: 'none', background: 'none', color: 'var(--text-faint)', cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      )}
      {employeesQuery.isError && !employeesQuery.data && (
        <div style={{ color: 'var(--danger-text, #dc2626)', marginBottom: '10px' }}>Failed to load employees</div>
      )}

      {employeesQuery.isPending ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <>
          {/* Still list everyone with no sites yet: this is the only table on the page, so it is also how
              an admin reaches a provider's card. */}
          {sites.length === 0 && (
            <div style={{ color: 'var(--text-muted)', marginBottom: '10px' }}>No site credentials or hospitals are recorded yet.</div>
          )}
          {renderMatrix('Anesthesiologists', doctors, 'var(--or-blue-mid)', 'doctor')}
          {renderMatrix('CRNAs', crnas, 'var(--amber)', 'nurse')}
        </>
      )}
    </div>
  );
};

export default CredentialsMatrix;
